import { HttpClient, HttpRequest, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  map,
  startWith,
  tap,
} from 'rxjs';
import { API_BASE_URL, API } from 'src/app/constants';
import {
  UserLoginDTO,
  UserRegisterDTO,
  UserTokenDetails,
} from '../models';
import { LoginResponse } from '../models/response';
import { CookieService } from 'ngx-cookie-service';
import { differenceInMinutes } from 'date-fns';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private user$: BehaviorSubject<UserTokenDetails | null> =
    new BehaviorSubject<UserTokenDetails | null>(null);

  public user = this.user$.asObservable().pipe(startWith(null));
  public isLoggedIn$ = new BehaviorSubject<boolean>(false);

  public get isMedikoUser() {
    const user = this.user$.getValue();
    if (user) {
      return user.roles.some((r) => r.name === 'STAFF');
    }
    return false;
  }

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private BASE_URL: string,
    private cookie: CookieService,
    private tokenExtractor: HttpXsrfTokenExtractor,
    private router: Router
  ) {
    if (this.jwtToken) {
      const user = localStorage.getItem('user');
      if (user) {
        this.user$.next(JSON.parse(user));
        this.isLoggedIn$.next(true);
      }
    }
  }

  login(loginData: UserLoginDTO) {
    const url = this.path(API.AUTH.LOGIN);
    return this.http
      .post<LoginResponse>(url, loginData, { withCredentials: true })
      .pipe(map((res) => this.onLogin(res)));
  }

  logout(sendRequest = true) {
    //const url = this.path(API.AUTH.LOGOUT);
    //if (sendRequest) {
    //  return this.http.post(url, {}).pipe(
    //    tap((_) => {
    //      localStorage.removeItem('token');
    //      localStorage.removeItem('expiration');
    //      this.isLoggedIn$.next(false);
    //    }),
    //    map((_) => this.user$.next(null))
    //  );
    //} else
    {
      this.cookie.delete('ACCESS-TOKEN');
      this.cookie.delete('REFRESH-TOKEN');

      localStorage.removeItem('token');
      localStorage.removeItem('expiration');
      this.user$.next(null);
      this.isLoggedIn$.next(false);
      this.router.navigateByUrl('/auth/login');
      return EMPTY;
    }
  }

  resetPassword(email: string) {
    return this.http.post(this.path(API.AUTH.RESET_PASSWORD), { email });
  }

  changePassword(data: any) {
    return this.http.post(this.path(API.AUTH.CHANGE_PASSWORD), data);
  }

  tokenNeedsRefresh() {
    console.log('checking refresh token');
    if (this.expirationDate) {
      const now = new Date();
      const expiration = new Date(this.expirationDate);

      const diff = differenceInMinutes(expiration, now);
      console.log(expiration, now);
      console.log(diff);
      if (diff < 240000) {
        return true;
      }
      return false;
    }
    return false;
  }

  onLogin(data: LoginResponse) {
    this.user$.next(data.userTokenDetails);
    localStorage.setItem('expiration', data.expireOn);
    this.isLoggedIn$.next(true);
    localStorage.setItem('user', JSON.stringify(data.userTokenDetails));
    this.router.navigateByUrl('/profile');
  }

  register(data: UserRegisterDTO) {
    const url = this.path(API.AUTH.REGISTER);
    return this.http
      .post<LoginResponse>(url, data)
      .pipe(map((user) => this.user$.next(user.userTokenDetails)));
  }

  get jwtToken() {
    return this.cookie.get('ACCESS-TOKEN');
  }

  get refToken() {
    return this.cookie.get('REFRESH-TOKEN');
  }

  get expirationDate() {
    return localStorage.getItem('expiration');
  }

  refreshToken() {
    const url = this.path(API.AUTH.REFRESH);
    return this.http.post<LoginResponse>(url, { refreshToken: this.refToken })
      .pipe(
        tap(res => {
          this.user$.next(res.userTokenDetails);
          localStorage.setItem('expiration', res.expireOn);
          localStorage.setItem('user', JSON.stringify(res.userTokenDetails));
        })
      );
  }

  public setRequestHeader(request: HttpRequest<any>) {
    
    let token = this.tokenExtractor.getToken() as string;
    if (token !== null) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.jwtToken}`,
          "X-XSRF-TOKEN": token
        },
      });
    }
    else {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.jwtToken}`
        },
      });
    }
  }

  private path(path: string) {
    return `${this.BASE_URL}${path}`;
  }
}
